import { Component, Input, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { ActionSheetController, AlertController, ModalController } from '@ionic/angular';

import { ProductCategory } from '../../models';
import { ProductCategoryService } from '../../services/product-category.service';
import { Utilities } from '../../utilities/utilities';
import { Observable, of } from 'rxjs';

export interface IProductCategoryFormModalCallBackData {
  productCategory: ProductCategory;
  action: string;
}

@Component({
  selector: 'app-category-form',
  templateUrl: './category-form.page.html',
  styleUrls: ['./category-form.page.scss'],
})
export class CategoryFormPage implements OnInit {
  public isLoading: boolean;

  public submitted: boolean;

  public errorOccurredWhileLoadingProductCategory: boolean;

  @Input() productCategoryID: number = 0;

  public productCategory: ProductCategory;

  public productCategoryForm: UntypedFormGroup;

  public productCategoryName: UntypedFormControl;

  public title: string;

  public action: string;

  constructor(
    private alertCtrl: AlertController,
    private actionsheetCtrl: ActionSheetController,
    private formBuilder: UntypedFormBuilder,
    private productCategoryService: ProductCategoryService,
    private modalCtrl: ModalController
  ) {
    this.submitted = false;
    this.isLoading = true;
    this.productCategoryName = new UntypedFormControl(
      '',
      Validators.compose([Validators.required, Validators.maxLength(50)])
    );
    this.productCategoryForm = this.formBuilder.group({
      name: this.productCategoryName,
    });
  }

  public ngOnInit() {
    this.refresh();
  }

  public refresh() {
    this.title = 'Create Product Category';
    this.productCategory = new ProductCategory({
      id: undefined,
      name: undefined
    })
    if (this.productCategoryID != 0) {
      this.isLoading = true;
      this.errorOccurredWhileLoadingProductCategory = false;
      this.title = 'Edit Product Category';
      this.loadProductCategory().subscribe({
        next: (productCategory: ProductCategory) => {
          this.productCategory = productCategory;
          this.productCategoryName.setValue(productCategory.getName());
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          this.errorOccurredWhileLoadingProductCategory = true;
        },
      });
    }
    this.isLoading = false;
  }

  public loadProductCategory(): Observable<ProductCategory> {
    if (this.productCategoryID) {
      this.action = 'update';

      return this.productCategoryService.loadProductCategoryByID(
        this.productCategoryID
      );
    } else {
      this.action = 'create';
      return of<ProductCategory>(
        new ProductCategory({
          id: undefined,
          name: '',
        })
      );
    }
  }

  public onDismiss() {
    this.modalCtrl.dismiss()
  }

  public onSave() {
    this.submitted = true;

    if (this.productCategoryForm.valid === false) {
      return;
    }

    let productCategory: ProductCategory = this.productCategory;
    productCategory = productCategory.setName(this.productCategoryName.value);

    this.isLoading = true;
    this.productCategoryService.save(productCategory).subscribe({
      next: (productCategory) => {
        let callBackData: IProductCategoryFormModalCallBackData = {
          productCategory: productCategory,
          action: this.action,
        };

        this.isLoading = false;
        this.modalCtrl.dismiss(callBackData)
      },
      error: async (error) => {
        this.isLoading = false;
        const alert = await this.alertCtrl.create(
          Utilities.getDefaultErrorMessageConfig()
        );
        alert.present();
      },
    });
  }

  public async onProductCategoryDelete() {
    const actionSheet = await this.actionsheetCtrl.create({
      header: 'Are you sure you want to delete this product category?',
      buttons: [
        {
          text: 'Yes, delete this product category',
          role: 'destructive',
          handler: () => {
            const navTransition = actionSheet.dismiss();
            this.productCategoryService
              .deleteProductCategory(this.productCategory)
              .subscribe({
                next: (result) => {
                  navTransition.then(() => {
                    let callBackData: IProductCategoryFormModalCallBackData = {
                      productCategory: this.productCategory,
                      action: 'delete',
                    };

                    this.modalCtrl.dismiss(callBackData)
                  });
                },
                error: async (error) => {
                  const alert = await this.alertCtrl.create(
                    Utilities.getDefaultErrorMessageConfig()
                  );
                  alert.present();
                },
              });

            return false;
          },
        },
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {},
        },
      ],
    });

    actionSheet.present();
  }
}
